import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCardActions } from '@angular/material/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { BaseSharedModule } from '../../../../../../../_base-shared/base-shared.module';
import { DepartmentCategory } from '../../../../../../../_base-shared/models/Department/DepartmentCategory';
import { User } from '../../../../../../../_base-shared/models/User/User';
import { MainGlobalEventService } from '../../../../_shared/services/main-global-event.service';
import { LoaderComponent } from '../../../../../../../_base-shared/components/loader/loader.component';
import { AccomplishmentService } from '../accomplishment.service';
import { AccomplishmentTarget } from '../../../../../../../_base-shared/models/Accomplishment/AccomplishmentTarget';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';

@Component({
  imports:     [
    BaseSharedModule,
    MatButton,
    MatCardActions,
    ReactiveFormsModule,
    TranslateModule,
    LoaderComponent
  ],
  selector:    'app-accomplishment-target-editor',
  templateUrl: './accomplishment-target-editor.component.html',
  styles:      [],
  standalone:  true,
})
export class AccomplishmentTargetEditorComponent implements OnInit, OnDestroy {
  public authUser: User;
  public isLoading                                      = 0;
  public categoryDepartments: Array<DepartmentCategory> = [];
  public form: FormGroup;
  public formActive                                     = false;
  public isSubmitting: boolean;
  public currentLanguage: 'en' | 'es';
  private subscriptions: Array<Subscription>            = [];

  constructor(private fb: UntypedFormBuilder,
              private cookieService: CookieService,
              private translate: TranslateService,
              private toastr: ToastrService,
              private globalEventsService: MainGlobalEventService,
              private accomplishmentService: AccomplishmentService) {
  }

  ngOnInit(): void {
    const storageLanguage = this.cookieService.get('lang');
    this.currentLanguage  = (storageLanguage === 'es' || storageLanguage === 'en') ? storageLanguage : 'es';
    this.subscriptions.push(
      this.translate.onLangChange.subscribe(next => {
        this.currentLanguage = (next.lang === 'es' || next.lang === 'en') ? next.lang : 'es';
      })
    );

    this.globalEventsService.authUser$.subscribe(user => {
      this.authUser = user;
      if (this.authUser) {
        this.fetchAccomplishmentTargets();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public departmentTargetsArray(): FormArray<any> {
    return this.form.get('department_targets') as FormArray;
  }

  public accomplishmentTargetsArray(index: number): any {
    return this.departmentTargetsArray().at(index).get('targets') as FormArray;
  }

  private fetchAccomplishmentTargets(): void {
    this.isLoading++;
    this.subscriptions.push(
      this.accomplishmentService.indexDepartmentAccomplishmentTargets({select_all: 1}, ['departments.accomplishment_targets'])
        .pipe(finalize(() => this.isLoading--))
        .subscribe({
          next:  result => {
            this.categoryDepartments = result.data;
            this.buildForm(this.categoryDepartments);
          },
          error: error => console.error(error)
        }),
    );
  }

  public submitForm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    this.subscriptions.push(
      this.accomplishmentService.updateDepartmentAccomplishmentTargets(this.form.value)
        .pipe(finalize(() => this.isSubmitting = false))
        .subscribe({
          next:     () => {
            this.toastr.success(this.translate.instant('CONFIG.system-event.config-saved'));
          }, error: () => {
            this.toastr.error(this.translate.instant('SHARED.went_wrong'));
          }
        }),
    );
  }

  private buildForm(categoryDepartments: DepartmentCategory[]): void {
    this.form                    = this.fb.group({
      department_targets: this.fb.array([]),
    });
    const departmentTargetsArray = this.form.get('department_targets') as FormArray;
    categoryDepartments.forEach(categoryDepartment => {
      if (categoryDepartment.departments?.length) {
        categoryDepartment.departments.forEach(department => {
          if (department?.accomplishment_targets?.length) {
            const departmentForm = this.fb.group({
              department: this.fb.group({
                id:      [department.id, Validators.required],
                name_en: [department.name_en, Validators.required],
                name_es: [department.name_es, Validators.required],
              }),
              targets:    this.fb.array([])
            });
            const targetsArray   = departmentForm.get('targets') as FormArray;
            department.accomplishment_targets.forEach(target => {
              if (target.is_active) {
                targetsArray.push(this.initTargetFormGroup(target));
              }
            });
            departmentTargetsArray.push(departmentForm);
          }
        });
      }
    });
    this.formActive = true;
  }

  private initTargetFormGroup(target: AccomplishmentTarget): FormGroup {
    return this.fb.group({
      id:      target.id,
      slug:    target.slug,
      name_en: target.name_en,
      name_es: target.name_es,
      goal:    target.goal,
    });
  }
}
